import DashboardIcon from "@mui/icons-material/Dashboard"
import ExpandLess from "@mui/icons-material/ExpandLess"
import visaIcon from "icons/visa.svg"
import ExpandMore from "@mui/icons-material/ExpandMore"
import PersonAddIcon from "@mui/icons-material/PersonAdd"
import userIcon from "icons/user.svg"
import helpDeskIcon from "icons/help-desk.svg"
import currencyIcon from "icons/currency.svg"
import { useCallback, useMemo, useState, useContext } from "react"
import { AppContext } from "./app-context-provider.js"
import { Link, useLocation } from "react-router-dom"
import { Layout, SlidePanel } from "widgets"
import { colors } from "design"
import { featuresFlatMap, belowViewPort } from "utils"
import { typography } from "../../design/typography.js"

const menuOptionStyle = {
  ":hover": {
    background: "rgba(66, 133, 244, 0.1294117647)",
    borderLeft: `4px solid ${colors.primaryBlueGrey}`,
    color: colors.primaryBlueGrey,
    svg: {
      fill: colors.primaryBlueGrey,
    },
  },
}

const selectedExpandableOptionStyle = {
  background: "rgba(66, 133, 244, 0.1294117647)",
  borderLeft: `4px solid ${colors.primaryBlueGrey}`,
  color: colors.primaryBlueGrey,
  svg: {
    fill: colors.primaryBlueGrey,
  },
}

const selectedOptionStyle = {
  color: colors.primaryBlueGrey,
  svg: {
    fill: colors.primaryBlueGrey,
  },
}

const linkStyle = {
  textDecoration: "none",
  border: "none",
  color: "unset",
  outline: "none",
  borderLeft: `4px solid transparent`,
}

const NavPanel = ({ onClose }) => {
  const location = useLocation()
  const [expandedOption, setExpandedOption] = useState(
    () => `/${_.compact(location.pathname.split("/"))[0]}`,
  )
  const appContext = useContext(AppContext)

  const features = useMemo(
    () => featuresFlatMap(appContext?.user?.featureDTOList),
    [appContext?.user?.featureDTOList],
  )

  const {
    accounts = [],
    agency = [],
    home = [],
    users = [],
    ["e-visa"]: eVisa = [],
    ["help desk"]: helpDesk = [],
    ["my requests"]: myRequests = [],
  } = features ?? {}

  const toggleMenuItem = useCallback(
    (link) => {
      if (expandedOption === link) return setExpandedOption(null)
      setExpandedOption(link)
    },
    [expandedOption],
  )

  const menuOptions = useMemo(
    () =>
      _.compact([
        home.dashboard && {
          text: "Dashboard",
          name: "dashboard",
          icon: <DashboardIcon style={{ height: 18, width: 18 }} />,
          link: "/",
          standalone: true,
        },
        (home["e-visa"] || eVisa["search-visa-transaction"]) && {
          text: "E-VISA",
          name: "e-visa",
          link: "/visa",
          icon: (
            <img
              src={visaIcon}
              alt="e-visa-icon"
              css={{ width: 18, height: 18 }}
            />
          ),
          onClick: () => toggleMenuItem("/visa"),
          show: expandedOption === "/visa",
          subMenu: _.compact([
            eVisa["visa-service"] && {
              name: "visa-service",
              text: "eVisa Service",
              isHide: false,
              link: "/visa/visa-service",
            },
            eVisa["visa"] && {
              name: "visa-service",
              text: "eVisa",
              isHide: false,
              link: "/visa/visa-service",
            },
            eVisa["issue-visa"] && {
              name: "issue-visa",
              text: "Issue eVisa",
              isHide: false,
              link: "/visa/issue-visa",
            },
            eVisa["track-visa"] && {
              name: "track-visa",
              text: "Track eVisa",
              isHide: false,
              link: "/visa/track-visa",
            },
            eVisa["search-visa-transaction"] && {
              text: "Manage eVisa Txn",
              name: "manage-visa-transactions",
              link: "/visa/manage-visa-transactions",
            },
            eVisa["search-my-visa-transactions"] && {
              text: "My eVisa Transactions",
              name: "manage-my-visa-transactions",
              link: "/visa/manage-my-visa-transactions",
            },
          ]),
        },

        _.size(agency) && {
          text: "Agency",
          name: "agency",
          link: "/agency",
          icon: (
            <PersonAddIcon
              style={{
                transform: "scaleX(-1)",
                fontSize: 30,
                height: 18,
                width: 18,
              }}
            />
          ),
          onClick: () => toggleMenuItem("/agency"),
          show: expandedOption === "/agency",
          subMenu: _.compact([
            agency["search-agency"] && {
              name: "manage-agency",
              text: "Manage Agency",
              isHide: false,
              link: "/agency/manage-agency",
            },
            agency["view-agency"] &&
              appContext?.user?.userBasicDTO?.masterUser === 1 && {
                //Level 2
                name: "view-agency",
                text: "View Agency",
                isHide: false,
                link: "/agency/view-my-agency",
              },
          ]),
        },
        _.size(accounts) && {
          text: "Accounts",
          name: "accounts",
          link: "/accounts",
          icon: (
            <img
              src={currencyIcon}
              alt="currency-icon"
              css={{ height: 18, width: 18 }}
            />
          ),
          onClick: () => toggleMenuItem("/accounts"),
          show: expandedOption === "/accounts",
          subMenu: _.compact([
            accounts["search-bank-account"] && {
              name: "manage-bank-accounts",
              text: "Bank Accounts",
              isHide: false,
              link: "/accounts/manage-bank-accounts",
            },
            accounts["add-agency-deposit"] && {
              name: "add-agency-deposit",
              text: "Add Deposit Request",
              isHide: false,
              link: "/accounts/add-agency-deposit",
            },
            accounts["search-agency-deposits"] && {
              name: "agency-deposit-requests",
              text: "Agency Deposit Requests",
              isHide: false,
              link: "/accounts/manage-deposit-requests",
            },
            accounts["bank-account-list"] && {
              name: "bank-account-list",
              text: "Bank Accounts List",
              isHide: false,
              link: "/accounts/list-bank-accounts",
            },
            accounts["search-my-deposits"] && {
              name: "search-my-deposits",
              text: t("deposit_requests.my_deposits"),
              isHide: false,
              link: "/accounts/my-deposits",
            },
            accounts["agency-ledger"] && {
              name: "agency-ledger",
              text: "Agency Ledger",
              isHide: false,
              link: "/accounts/agency-ledger",
            },
            accounts["my-ledger"] && {
              name: "my-ledger",
              text: "My Ledger",
              isHide: false,
              link: "/accounts/my-ledger",
            },
            accounts["search-supplier"] && {
              name: "supplier",
              text: "Manage Supplier",
              isHide: false,
              link: "/accounts/manage-suppliers",
            },
            accounts["purchase-ledger"] && {
              name: "purchase-ledger",
              text: "Purchase Ledger",
              isHide: false,
              link: "/accounts/purchase-ledger",
            },
            accounts["sales-ledger"] && {
              name: "sale-ledger",
              text: "Sale Ledger",
              isHide: false,
              link: "/accounts/sale-ledger",
            },
            accounts["profit-loss"] && {
              name: "profit-and-loss",
              text: "Profit & Loss",
              isHide: false,
              link: "/accounts/profit-and-loss",
            },
          ]),
        },
        _.size(helpDesk) && {
          text: "Help Desk",
          name: "helpDesk",
          link: "/help-desk",
          icon: (
            <img
              src={helpDeskIcon}
              alt="help-desk-icon"
              css={{ height: 18, width: 18 }}
            />
          ),
          onClick: () => toggleMenuItem("/help-desk"),
          show: expandedOption === "/help-desk",
          subMenu: _.compact([
            helpDesk["search-agency-requests"] && {
              name: "agency requests",
              text: "Agency Requests",
              isHide: false,
              link: "/help-desk/manage-agency-requests",
            },
          ]),
        },

        _.size(myRequests) && {
          text: "My Requests",
          name: "my-requests",
          link: "/my-requests",
          icon: (
            <img
              src={userIcon}
              alt="user-icon"
              css={{ height: 18, width: 18 }}
            />
          ),
          onClick: () => toggleMenuItem("/my-requests"),
          show: expandedOption === "/my-requests",
          subMenu: _.compact([
            myRequests["search-my-requests"] && {
              name: "search-my-requests",
              text: "Search My Requests",
              isHide: false,
              link: "/help-desk/my-requests",
            },
          ]),
        },

        _.size(users) && {
          text: "User",
          name: "user",
          link: "/user",
          icon: (
            <img
              src={userIcon}
              alt="user-icon"
              css={{ height: 18, width: 18 }}
            />
          ),
          onClick: () => toggleMenuItem("/user"),
          show: expandedOption === "/user",
          subMenu: _.compact([
            users["search-user"] && {
              name: "manage-user",
              text: "Manage User",
              isHide: false,
              link: "/user/manage-user",
            },
            users["create-agency-user"] && {
              name: "manage-user",
              text: "Create Agency User",
              isHide: false,
              link: "/user/create-agency-user",
            },
            users["search-security-group"] && {
              name: "manage-security-group",
              text: "Security Group",
              isHide: false,
              link: "/user/manage-security-group",
            },
          ]),
        },
      ]),
    [
      accounts,
      agency,
      appContext?.user?.userBasicDTO?.masterUser,
      eVisa,
      expandedOption,
      helpDesk,
      home,
      myRequests,
      toggleMenuItem,
      users,
    ],
  )

  return (
    <SlidePanel
      onClose={onClose}
      floatingStyles={{ minWidth: 280 }}
      contentStyle={{ height: "100%" }}
    >
      <nav
        className="nav-menu"
        css={{
          minWidth: 280,
          height: "100%",
          overflow: "auto",
          color: colors.darkGrey100,
          background: colors.cream,
        }}
      >
        {_.map(
          menuOptions,
          (
            { icon, link, text, subMenu, customStyle, onClick, standalone },
            index,
          ) =>
            !standalone && !_.size(subMenu) ? null : (
              <div key={index}>
                <Layout
                  className="nav-menu-item"
                  onClick={onClick}
                  css={[
                    {
                      gap: 20,
                      padding: "13px 22px",
                      borderLeft: "4px solid transparent",
                      background: colors.cream,
                    },
                    menuOptionStyle,
                    expandedOption === link && selectedExpandableOptionStyle,
                    customStyle,
                  ]}
                >
                  {icon}
                  {_.size(subMenu) ? (
                    <div
                      className="expandable-option"
                      css={[
                        {
                          fontWeight: 500,
                        },
                        typography.navMenuItemStyle,
                      ]}
                    >
                      {text}
                    </div>
                  ) : (
                    <Link
                      to={link || "/"}
                      css={[
                        typography.navMenuItemStyle,
                        linkStyle,
                        location.pathname === link && selectedOptionStyle,
                      ]}
                      onClick={onClose}
                    >
                      {text}
                    </Link>
                  )}
                  {_.size(subMenu) ? (
                    expandedOption === link ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )
                  ) : null}
                </Layout>
                {_.size(subMenu) ? (
                  <Layout
                    className="sub-menu"
                    css={[
                      {
                        maxHeight: 0,
                        overflow: "hidden",
                        transition: "max-height 0.2s ease-in-out",
                        background: colors.cream,
                      },
                      expandedOption === link && { maxHeight: "100vh" },
                    ]}
                    vertical
                  >
                    {_.map(
                      subMenu,
                      (
                        { text: menuItemText, link: menuItemLink, isHide },
                        subrowIndex,
                      ) =>
                        !isHide ? (
                          <Link
                            key={subrowIndex}
                            to={menuItemLink}
                            onClick={onClose}
                            css={[
                              {
                                gap: 20,
                                padding: "13px 22px",
                                borderLeft: "4px solid transparent",
                                paddingLeft: 65,
                                width: "100%",
                                fontSize: "1rem",
                              },
                              belowViewPort(1441, { fontSize: "0.875rem" }),
                              menuOptionStyle,
                              linkStyle,
                              _.includes(location.pathname, menuItemLink) &&
                                selectedOptionStyle,
                            ]}
                          >
                            {menuItemText}
                          </Link>
                        ) : null,
                    )}
                  </Layout>
                ) : null}
              </div>
            ),
        )}
      </nav>
    </SlidePanel>
  )
}

export default NavPanel
